/**
 * useUsers Hook
 * React Query hook for user management operations
 */

import { useQuery } from '@tanstack/react-query';
import { getUsers, type GetUsersParams } from '../../api/endpoints/users';
import { TokenManager } from '../../api/interceptors';

// =============================================================================
// REACT QUERY HOOKS
// =============================================================================

/**
 * Hook to fetch users with filtering
 * @param params - Query parameters for filtering users
 * @returns React Query result with users data
 */
export function useUsers(params: GetUsersParams = {}) {
  return useQuery({
    queryKey: ['users', params],
    queryFn: async () => {
      const token = TokenManager.getAccessToken();
      if (!token) {
        throw new Error('No authentication token available');
      }
      
      const response = await getUsers(params, token);
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Failed to fetch users');
      }
      // Return unwrapped data for consistent structure with admin hook
      // Backend returns: { success: true, data: { users: [], pagination: {} } }
      // We return: { users: [], pagination: {} }
      return response.data;
    },
    retry: 2,
    retryDelay: 1000,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
